/**
 * ============================================================
 * HeritagKosh \u2014 Festivals of India
 * File: assets/festivals-india.js
 * Version: 1.0.0
 * Dependencies: None (vanilla JS only)
 * Requires: window.festivalsData + window.festivalsConfig
 *           (injected by festivals-india.liquid)
 * ============================================================
 *
 * MODULES:
 *  1. MapController      \u2014 SVG map hover/click/tooltip
 *  2. FilterController   \u2014 Month + category filtering
 *  3. CardRenderer       \u2014 Renders festival cards into grid
 *  4. DetailPanel        \u2014 Slide-in detail panel + tabs
 *  5. ProductLoader      \u2014 Fetches products via Shopify Search API
 *  6. Utils              \u2014 Shared helpers
 *  7. Init               \u2014 Bootstraps everything
 *
 * ARCHITECTURE:
 *  - AppState object holds current filter state + selected festival
 *  - filterAndRender() is the single re-render entry point
 *  - All DOM manipulation is contained within each module
 * ============================================================
 */

(function () {
  'use strict';

  /* \u2500\u2500 GUARD: Wait for config \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
  if (!window.festivalsConfig) {
    console.warn('[HeritagKosh Festivals] window.festivalsConfig not found. Section will not initialise.');
    return;
  }

  const CONFIG  = window.festivalsConfig; // Section config from Liquid
  let   DATA    = window.festivalsData || [];  // May be partial (Liquid 50-item limit)

  /* \u2500\u2500 APP STATE \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
  const AppState = {
    selectedState:    null,   // e.g. 'rajasthan' | null = all
    selectedCategory: '',     // '' = all
    selectedMonth:    '',     // '' = all, '1'-'12'
    currentFestivals: [],     // filtered array currently displayed
    detailIndex:      -1,     // index in currentFestivals for detail panel
    detailFestival:   null,   // festival object in detail panel
  };

  /* ============================================================
     UTILS
  ============================================================ */
  const Utils = {

    /** Format a YYYY-MM-DD string to "Monday, 14 October 2025" */
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      if (isNaN(d)) return dateStr;
      return d.toLocaleDateString('en-IN', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    },

    /** Short format: "14 Oct 2025" */
    formatDateShort(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      if (isNaN(d)) return dateStr;
      return d.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    },

    /** Get month number (1-12) from YYYY-MM-DD */
    getMonth(dateStr) {
      if (!dateStr) return null;
      const d = new Date(dateStr + 'T00:00:00');
      if (isNaN(d)) return null;
      return d.getMonth() + 1;
    },

    /** Return CSS class suffix for category badge */
    categoryClass(cat) {
      const map = {
        'hindu':    'hindu',
        'national': 'national',
        'islamic':  'islamic',
        'sikh':     'sikh',
        'christian':'christian',
        'buddhist': 'buddhist',
        'jain':     'jain',
        'regional': 'regional',
      };
      return map[(cat || '').toLowerCase()] || 'regional';
    },

    /** Select a respectful celebration motif for cards and map markers. */
    festivalMotifType(festival) {
      const name = ((festival && festival.name) || '').toLowerCase();
      const cat  = ((festival && festival.category) || '').toLowerCase();
      if (/diwali|deepavali|deepam|deepotsav|kali puja/.test(name)) return 'diya';
      if (/dussehra|dasara|durga|navratri|bonalu|gangaur|attukal/.test(name)) return 'durga';
      if (/holi|shigmo/.test(name)) return 'holi';
      if (/janmashtami|krishna|govardhan/.test(name)) return 'flute';
      if (/ganesh|vinayaka/.test(name)) return 'modak';
      if (/raksha|rakhi|bhai dooj/.test(name)) return 'rakhi';
      if (/pongal|sankranti|bihu|onam|baisakhi|harvest|nuakhai/.test(name)) return 'harvest';
      if (/shivaratri|shiva|maha shiv/.test(name)) return 'damaru';
      if (/ram navami|ramayana|rath yatra/.test(name)) return 'bow';
      if (/eid|ramadan|muharram|milad/.test(name) || cat === 'islamic') return 'lantern';
      if (/christmas|easter|feast|good friday/.test(name) || cat === 'christian') return 'star';
      if (/republic|independence|gandhi|ambedkar/.test(name) || cat === 'national') return 'flag';
      if (/guru|gurpurab|hola mohalla/.test(name) || cat === 'sikh') return 'drum';
      if (/buddha|losar|hemis|torgya/.test(name) || cat === 'buddhist' || cat === 'jain') return 'lotus';
      if (/dance|jatra|fair|mela|utsav|carnival|festival/.test(name) || cat === 'regional') return 'drum';
      return 'flower';
    },

    /** Inline line-art used beside festival names; decorative and screen-reader silent. */
    festivalSketch(festival, compact) {
      const type = this.festivalMotifType(festival);
      const motifs = {
        diya: '<path d="M9 32 Q24 40 39 32 Q35 45 24 46 Q13 45 9 32Z"/><path d="M24 30 C17 22 25 14 24 8 C33 17 32 25 24 30Z"/><path d="M7 19l-4-3M41 19l4-3M12 10L9 5M36 10l3-5"/>',
        durga: '<path d="M7 27 Q15 16 23 27 Q15 34 7 27ZM25 27 Q33 16 41 27 Q33 34 25 27Z"/><path d="M14 27h3M31 27h3"/><circle cx="24" cy="15" r="2.5"/><path d="M12 14 Q24 3 36 14M24 6V1M18 8l-3-5M30 8l3-5M24 33q-5 6 0 11q5-5 0-11Z"/>',
        holi: '<path d="M8 36l22-22M27 11l6 6M6 38l6 6M31 12l7-7M35 8l5 5"/><path d="M27 40 Q34 32 41 40 L39 45H29Z"/><circle cx="12" cy="15" r="3"/><circle cx="20" cy="8" r="2"/>',
        flute: '<path d="M7 36L39 12M12 34l-4-5M19 29l-4-5M27 23l-4-5M35 17l-4-5"/><circle cx="18" cy="27" r="1.4"/><circle cx="25" cy="22" r="1.4"/><path d="M35 11Q42 3 44 11Q42 18 35 11ZM38 9q2 2 4 0"/>',
        modak: '<path d="M24 5q3 5 8 8q8 7 8 17q0 14-16 16Q8 44 8 30q0-10 8-17q5-3 8-8Z"/><path d="M16 17q8 6 16 0M24 13v28M16 24q4 4 8 4t8-4"/>',
        rakhi: '<path d="M2 24h12M34 24h12"/><circle cx="24" cy="24" r="10"/><circle cx="24" cy="24" r="5"/><path d="M24 10l3 5l6-1l-1 6l5 4l-5 4l1 6l-6-1l-3 5l-3-5l-6 1l1-6l-5-4l5-4l-1-6l6 1Z"/>',
        harvest: '<path d="M24 44V8M24 18Q14 12 10 5M24 26Q34 20 38 13M24 34Q14 29 9 22"/><path d="M14 11q-7 1-8-5q6-1 8 5ZM34 19q7 0 8-6q-6-1-8 6ZM14 29q-7 0-8-6q6-1 8 6Z"/>',
        damaru: '<path d="M12 10Q24 19 36 10L34 4H14ZM12 38Q24 29 36 38L34 44H14ZM18 15v18M30 15v18"/><path d="M18 24h12M12 24l-6-4M36 24l6 4"/><circle cx="5" cy="19" r="2"/><circle cx="43" cy="29" r="2"/>',
        bow: '<path d="M12 5Q37 24 12 43M12 5L12 43M4 24h37M35 18l7 6l-7 6"/>',
        lantern: '<path d="M17 11h14M19 7h10M18 13l-5 9l5 15h12l5-15l-5-9ZM18 37h12M24 37v7"/><path d="M18 22h12M16 28h16"/><circle cx="24" cy="25" r="3"/>',
        star: '<path d="M24 4l5.5 13h14l-11 8.5L36 40l-12-8l-12 8l3.5-14.5L4 17h14Z"/><path d="M24 0v4M44 8l-4 4M4 8l4 4"/>',
        flag: '<path d="M12 45V5M13 7Q27 2 39 9Q29 18 13 14Z"/><path d="M13 10h26M13 12h20"/>',
        drum: '<path d="M10 14Q24 8 38 14L35 38Q24 44 13 38Z"/><path d="M10 14q14 8 28 0M13 38q11-8 22 0M14 18l20 16M34 18L14 34"/><path d="M7 9l8 9M41 9l-8 9"/>',
        lotus: '<path d="M24 39Q12 28 24 10Q36 28 24 39ZM24 39Q5 36 7 20Q20 20 24 39ZM24 39Q43 36 41 20Q28 20 24 39Z"/><path d="M7 41q17 5 34 0"/>',
        flower: '<circle cx="24" cy="24" r="5"/><path d="M24 19Q15 4 24 3Q33 4 24 19ZM29 24Q44 15 45 24Q44 33 29 24ZM24 29Q33 44 24 45Q15 44 24 29ZM19 24Q4 33 3 24Q4 15 19 24Z"/>'
      };
      return '<span class="fi-festival-sketch fi-festival-sketch--' + type + (compact ? ' fi-festival-sketch--compact' : '') + '" aria-hidden="true" data-motif="' + type + '"><svg viewBox="0 0 48 48" focusable="false">' + (motifs[type] || motifs.flower) + '</svg></span>';
    },

    /**
     * Generate an animated SVG banner for a festival based on its category.
     * Returns full HTML string for the banner element.
     */
    categoryBanner(festival) {
      const cat = (festival.category || '').toLowerCase();
      const name = festival.name || '';
      const uid  = 'fi-anim-' + (festival.slug || Math.random().toString(36).slice(2));

      const banners = {
        hindu: `
          <div class="fi-animated-banner fi-animated-banner--hindu" aria-hidden="true" role="img" aria-label="${Utils.escapeHtml(name)} festival banner">
            <style>
              @keyframes ${uid}-flamb{0%,100%{transform:scaleY(1) rotate(-2deg)}50%{transform:scaleY(1.18) rotate(2deg)}}
              @keyframes ${uid}-glow{0%,100%{opacity:.7}50%{opacity:1}}
              @keyframes ${uid}-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
              @keyframes ${uid}-sparkle{0%,100%{opacity:0;transform:scale(0)}40%{opacity:1;transform:scale(1)}70%{opacity:.5}}
            </style>
            <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
              <defs>
                <radialGradient id="${uid}-bg" cx="50%" cy="60%" r="70%">
                  <stop offset="0%" stop-color="#7C3AED" stop-opacity=".18"/>
                  <stop offset="100%" stop-color="#1a0a2e" stop-opacity=".05"/>
                </radialGradient>
                <radialGradient id="${uid}-flame-g" cx="50%" cy="80%" r="60%">
                  <stop offset="0%" stop-color="#FFD700"/>
                  <stop offset="60%" stop-color="#FF6B00"/>
                  <stop offset="100%" stop-color="#CC2200" stop-opacity="0"/>
                </radialGradient>
              </defs>
              <rect width="400" height="200" fill="url(#${uid}-bg)"/>
              <!-- Diyas row -->
              ${[60,130,200,270,340].map((x,i) => `
                <g transform="translate(${x},${130+Math.sin(i)*8})" style="animation:${uid}-float ${1.8+i*0.15}s ease-in-out infinite;animation-delay:${i*0.2}s">
                  <ellipse cx="0" cy="18" rx="20" ry="6" fill="#C8860A" opacity=".9"/>
                  <path d="M-18 14 Q0 4 18 14 Q16 20 0 22 Q-16 20 -18 14Z" fill="#E8A020"/>
                  <path d="M-14 14 Q0 6 14 14 Q12 19 0 21 Q-12 19 -14 14Z" fill="#F5B830"/>
                  <path d="M0 10 Q3 2 0 -14 Q-3 2 0 10Z" fill="url(#${uid}-flame-g)" style="transform-origin:0 10px;animation:${uid}-flame ${0.9+i*0.07}s ease-in-out infinite;animation-delay:${i*0.1}s"/>
                  <ellipse cx="0" cy="10" rx="3" ry="4" fill="#FFF7E6" opacity=".6" style="animation:${uid}-glow 1.2s ease-in-out infinite;animation-delay:${i*0.15}s"/>
                </g>
              `).join('')}
              <!-- Rangoli dots -->
              ${[80,160,240,320].map((x,i) => `<circle cx="${x}" cy="175" r="3" fill="#FF6B9D" opacity=".7" style="animation:${uid}-sparkle ${1.5+i*0.3}s ease-in-out infinite;animation-delay:${i*0.4}s"/>`).join('')}
              <!-- Om symbol -->
              <text x="200" y="80" text-anchor="middle" font-size="52" fill="#D4AF37" opacity=".18" font-family="serif">\u0950</text>
              <!-- Festival name -->
              <text x="200" y="52" text-anchor="middle" font-size="15" fill="#7C3AED" opacity=".6" font-family="Georgia,serif" font-style="italic">${Utils.escapeHtml(name)}</text>
            </svg>
          </div>`,

        national: `
          <div class="fi-animated-banner fi-animated-banner--national" aria-hidden="true" role="img" aria-label="${Utils.escapeHtml(name)} festival banner">
            <style>
              @keyframes ${uid}-wave{0%{transform:rotate(-2deg) translateX(0)}50%{transform:rotate(2deg) translateX(4px)}100%{transform:rotate(-2deg) translateX(0)}}
              @keyframes ${uid}-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
              @keyframes ${uid}-pulse{0%,100%{opacity:.8}50%{opacity:1}}
            </style>
            <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
              <defs>
                <linearGradient id="${uid}-tri" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#FF9933"/>
                  <stop offset="33%" stop-color="#FF9933"/>
                  <stop offset="33%" stop-color="#ffffff"/>
                  <stop offset="66%" stop-color="#ffffff"/>
                  <stop offset="66%" stop-color="#138808"/>
                  <stop offset="100%" stop-color="#138808"/>
                </linearGradient>
              </defs>
              <rect width="400" height="200" fill="#F8F4EE" opacity=".4"/>
              <!-- Flag -->
              <g style="transform-origin:60px 30px;animation:${uid}-wave 2.5s ease-in-out infinite">
                <rect x="60" y="30" width="180" height="120" fill="url(#${uid}-tri)" rx="2" opacity=".85"/>
                <!-- Ashoka chakra -->
                <circle cx="150" cy="90" r="22" fill="none" stroke="#000080" stroke-width="2" opacity=".7" style="transform-origin:150px 90px;animation:${uid}-spin 6s linear infinite"/>
                ${Array.from({length:24},(_,i)=>{const a=i*15*Math.PI/180;return `<line x1="150" y1="90" x2="${150+19*Math.cos(a)}" y2="${90+19*Math.sin(a)}" stroke="#000080" stroke-width="1" opacity=".6"/>`}).join('')}
                <circle cx="150" cy="90" r="4" fill="#000080" opacity=".7"/>
              </g>
              <rect x="55" y="28" width="4" height="165" fill="#8B6914"/>
              <!-- Stars -->
              ${[[310,40],[340,80],[320,130],[350,150],[295,100]].map(([x,y],i)=>`<text x="${x}" y="${y}" text-anchor="middle" font-size="14" fill="#FF9933" opacity=".4" style="animation:${uid}-pulse ${1.5+i*0.3}s ease-in-out infinite;animation-delay:${i*0.2}s">\u2726</text>`).join('')}
              <text x="200" y="175" text-anchor="middle" font-size="13" fill="#000080" opacity=".4" font-family="Georgia,serif" font-style="italic">${Utils.escapeHtml(name)}</text>
            </svg>
          </div>`,

        islamic: `
          <div class="fi-animated-banner fi-animated-banner--islamic" aria-hidden="true" role="img" aria-label="${Utils.escapeHtml(name)} festival banner">
            <style>
              @keyframes ${uid}-moon{0%,100%{transform:rotate(-5deg)}50%{transform:rotate(5deg)}}
              @keyframes ${uid}-star{0%,100%{opacity:.5;transform:scale(.9)}50%{opacity:1;transform:scale(1.1)}}
              @keyframes ${uid}-lantern{0%,100%{transform:translateY(0) rotate(-3deg)}50%{transform:translateY(-8px) rotate(3deg)}}
            </style>
            <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
              <defs>
                <radialGradient id="${uid}-sky" cx="50%" cy="40%" r="70%">
                  <stop offset="0%" stop-color="#0a1628"/>
                  <stop offset="100%" stop-color="#1a2a4a"/>
                </radialGradient>
              </defs>
              <rect width="400" height="200" fill="url(#${uid}-sky)" opacity=".25"/>
              <!-- Stars scattered -->
              ${[[40,30],[90,20],[170,35],[260,15],[320,28],[370,40],[50,80],[150,65],[340,70],[380,90]].map(([x,y],i)=>`
                <circle cx="${x}" cy="${y}" r="${0.8+Math.random()*1.2}" fill="#FFE680" opacity="${0.4+i%3*0.2}" style="animation:${uid}-star ${1+i*0.3}s ease-in-out infinite;animation-delay:${i*0.15}s"/>
              `).join('')}
              <!-- Crescent moon -->
              <g style="transform-origin:200px 80px;animation:${uid}-moon 4s ease-in-out infinite">
                <circle cx="200" cy="80" r="50" fill="#D4AF37" opacity=".85"/>
                <circle cx="218" cy="68" r="42" fill="#F5F0E8" opacity=".95"/>
                <polygon points="235,55 240,70 255,65" fill="#D4AF37" opacity=".9" style="transform-origin:240px 65px;animation:${uid}-star 2s ease-in-out infinite"/>
              </g>
              <!-- Lanterns -->
              ${[[80,110],[320,110],[50,150],[350,145]].map(([x,y],i)=>`
                <g transform="translate(${x},${y})" style="animation:${uid}-lantern ${2+i*0.3}s ease-in-out infinite;animation-delay:${i*0.4}s">
                  <rect x="-10" y="0" width="20" height="28" rx="4" fill="#C41E3A" opacity=".7"/>
                  <rect x="-8" y="2" width="16" height="24" rx="3" fill="#FF4444" opacity=".4"/>
                  <line x1="0" y1="-10" x2="0" y2="0" stroke="#8B6914" stroke-width="2"/>
                  <circle cx="0" cy="28" r="3" fill="#FFD700" opacity=".8"/>
                </g>
              `).join('')}
              <text x="200" y="185" text-anchor="middle" font-size="13" fill="#D4AF37" opacity=".5" font-family="Georgia,serif" font-style="italic">${Utils.escapeHtml(name)}</text>
            </svg>
          </div>`,

        sikh: `
          <div class="fi-animated-banner fi-animated-banner--sikh" aria-hidden="true" role="img" aria-label="${Utils.escapeHtml(name)} festival banner">
            <style>
              @keyframes ${uid}-rotate{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
              @keyframes ${uid}-glow{0%,100%{filter:drop-shadow(0 0 4px #D4AF37)}50%{filter:drop-shadow(0 0 12px #D4AF37)}}
              @keyframes ${uid}-rise{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
            </style>
            <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
              <defs>
                <radialGradient id="${uid}-bg2" cx="50%" cy="50%" r="60%">
                  <stop offset="0%" stop-color="#1A6B3C" stop-opacity=".15"/>
                  <stop offset="100%" stop-color="#0D3320" stop-opacity=".05"/>
                </radialGradient>
              </defs>
              <rect width="400" height="200" fill="url(#${uid}-bg2)"/>
              <!-- Khanda symbol (stylized) -->
              <g transform="translate(200,100)" style="animation:${uid}-rise 3s ease-in-out infinite;animation-delay:.3s">
                <g style="animation:${uid}-glow 2s ease-in-out infinite">
                  <!-- Central double-edged sword -->
                  <path d="M0,-55 L6,-10 L6,40 L0,55 L-6,40 L-6,-10 Z" fill="#D4AF37" opacity=".9"/>
                  <!-- Chakkar (circle) -->
                  <circle cx="0" cy="0" r="30" fill="none" stroke="#D4AF37" stroke-width="5" opacity=".85" style="transform-origin:0 0;animation:${uid}-rotate 8s linear infinite"/>
                  <!-- Kirpans -->
                  <path d="M-50,-45 Q-30,0 -50,45 L-44,45 Q-22,0 -44,-45Z" fill="#D4AF37" opacity=".8"/>
                  <path d="M50,-45 Q30,0 50,45 L44,45 Q22,0 44,-45Z" fill="#D4AF37" opacity=".8"/>
                </g>
              </g>
              <!-- Decorative dots -->
              ${[60,160,240,340].map((x,i)=>`<circle cx="${x}" cy="170" r="4" fill="#D4AF37" opacity=".5" style="animation:${uid}-glow ${1.5+i*0.2}s ease-in-out infinite;animation-delay:${i*0.3}s"/>`).join('')}
              <text x="200" y="188" text-anchor="middle" font-size="13" fill="#1A6B3C" opacity=".5" font-family="Georgia,serif" font-style="italic">${Utils.escapeHtml(name)}</text>
            </svg>
          </div>`,

        christian: `
          <div class="fi-animated-banner fi-animated-banner--christian" aria-hidden="true" role="img" aria-label="${Utils.escapeHtml(name)} festival banner">
            <style>
              @keyframes ${uid}-twinkle{0%,100%{opacity:.3;transform:scale(.8)}50%{opacity:1;transform:scale(1.2)}}
              @keyframes ${uid}-bell{0%,100%{transform:rotate(-15deg)}50%{transform:rotate(15deg)}}
              @keyframes ${uid}-snow{from{transform:translateY(-10px) rotate(0)}to{transform:translateY(210px) rotate(360deg)}}
            </style>
            <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
              <rect width="400" height="200" fill="#E8F4FD" opacity=".3"/>
              <!-- Star of Bethlehem -->
              <g transform="translate(200,60)" style="animation:${uid}-twinkle 1.5s ease-in-out infinite">
                <polygon points="0,-30 7,-10 28,-10 12,4 18,24 0,12 -18,24 -12,4 -28,-10 -7,-10" fill="#FFD700" opacity=".9"/>
                <!-- Rays -->
                ${Array.from({length:8},(_,i)=>{const a=i*45*Math.PI/180;return `<line x1="0" y1="0" x2="${34*Math.cos(a)}" y2="${34*Math.sin(a)}" stroke="#FFD700" stroke-width="1.5" opacity=".4"/>`}).join('')}
              </g>
              <!-- Bells -->
              ${[[100,90],[300,90]].map(([x,y],i)=>`
                <g transform="translate(${x},${y})" style="transform-origin:${x}px ${y-15}px;animation:${uid}-bell ${1.2+i*0.2}s ease-in-out infinite;animation-delay:${i*0.4}s">
                  <path d="M0,-15 Q20,-15 20,10 Q20,25 0,25 Q-20,25 -20,10 Q-20,-15 0,-15Z" fill="#D4AF37" opacity=".8"/>
                  <circle cx="0" cy="25" r="5" fill="#8B6914" opacity=".9"/>
                  <line x1="0" y1="-15" x2="0" y2="-22" stroke="#8B6914" stroke-width="2"/>
                </g>
              `).join('')}
              <!-- Snowflakes -->
              ${[[70,40],[150,20],[250,35],[330,25],[380,60]].map(([x,y],i)=>`
                <text x="${x}" y="${y}" text-anchor="middle" font-size="${10+i%3*4}" fill="#4A90D9" opacity=".5" style="animation:${uid}-twinkle ${1+i*0.4}s ease-in-out infinite;animation-delay:${i*0.3}s">\u2744</text>
              `).join('')}
              <!-- Candles -->
              ${[[60,140],[160,140],[240,140],[340,140]].map(([x,y],i)=>`
                <g transform="translate(${x},${y})">
                  <rect x="-5" y="0" width="10" height="30" rx="2" fill="#FFF5E4" opacity=".8"/>
                  <path d="M0,0 Q3,-8 0,-18 Q-3,-8 0,0Z" fill="#FF6B00" opacity=".9" style="animation:${uid}-twinkle ${0.8+i*0.1}s ease-in-out infinite;animation-delay:${i*0.2}s"/>
                </g>
              `).join('')}
              <text x="200" y="188" text-anchor="middle" font-size="13" fill="#2255AA" opacity=".4" font-family="Georgia,serif" font-style="italic">${Utils.escapeHtml(name)}</text>
            </svg>
          </div>`,

        buddhist: `
          <div class="fi-animated-banner fi-animated-banner--buddhist" aria-hidden="true" role="img" aria-label="${Utils.escapeHtml(name)} festival banner">
            <style>
              @keyframes ${uid}-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
              @keyframes ${uid}-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
              @keyframes ${uid}-ripple{0%{r:5;opacity:.8}100%{r:40;opacity:0}}
            </style>
            <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
              <defs>
                <radialGradient id="${uid}-lotus-bg" cx="50%" cy="60%" r="50%">
                  <stop offset="0%" stop-color="#FF8C42" stop-opacity=".12"/>
                  <stop offset="100%" stop-color="#7B2D8B" stop-opacity=".05"/>
                </radialGradient>
              </defs>
              <rect width="400" height="200" fill="url(#${uid}-lotus-bg)"/>
              <!-- Dharma wheel -->
              <g transform="translate(200,95)" style="animation:${uid}-spin 10s linear infinite">
                <circle cx="0" cy="0" r="55" fill="none" stroke="#D4AF37" stroke-width="4" opacity=".6"/>
                <circle cx="0" cy="0" r="12" fill="#D4AF37" opacity=".7"/>
                ${Array.from({length:8},(_,i)=>{const a=i*45*Math.PI/180;return `<line x1="${13*Math.cos(a)}" y1="${13*Math.sin(a)}" x2="${52*Math.cos(a)}" y2="${52*Math.sin(a)}" stroke="#D4AF37" stroke-width="3" opacity=".7"/>`}).join('')}
                ${Array.from({length:8},(_,i)=>{const a=(i*45+22.5)*Math.PI/180;return `<path d="M${28*Math.cos(a)},${28*Math.sin(a)} Q${44*Math.cos(a-0.3)},${44*Math.sin(a-0.3)} ${52*Math.cos(a)},${52*Math.sin(a)} Q${44*Math.cos(a+0.3)},${44*Math.sin(a+0.3)} ${28*Math.cos(a)},${28*Math.sin(a)}Z" fill="#D4AF37" opacity=".5"/>`}).join('')}
              </g>
              <!-- Lotus petals below -->
              <g transform="translate(200,175)" style="animation:${uid}-float 3s ease-in-out infinite">
                ${[-40,-20,0,20,40].map((x,i)=>`<ellipse cx="${x}" cy="${-i%2*8}" rx="12" ry="18" fill="#FF8C9E" opacity="${0.3+i*0.08}" transform="rotate(${x/2},${x},0)"/>`).join('')}
              </g>
              <!-- Prayer flags -->
              ${['#FF0000','#FFFF00','#0000FF','#FFFFFF','#00AA00'].map((c,i)=>`
                <rect x="${30+i*60}" y="10" width="45" height="28" fill="${c}" opacity=".3" rx="2"/>
                <line x1="${30+i*60}" y1="10" x2="${75+i*60}" y2="10" stroke="#8B6914" stroke-width="1.5"/>
              `).join('')}
              <line x1="30" y1="10" x2="330" y2="10" stroke="#8B6914" stroke-width="1.5" opacity=".5"/>
              <text x="200" y="195" text-anchor="middle" font-size="12" fill="#7B2D8B" opacity=".4" font-family="Georgia,serif" font-style="italic">${Utils.escapeHtml(name)}</text>
            </svg>
          </div>`,

        regional: `
          <div class="fi-animated-banner fi-animated-banner--regional" aria-hidden="true" role="img" aria-label="${Utils.escapeHtml(name)} festival banner">
            <style>
              @keyframes ${uid}-sway{0%,100%{transform:rotate(-8deg)}50%{transform:rotate(8deg)}}
              @keyframes ${uid}-bloom{0%{transform:scale(.85)}50%{transform:scale(1.05)}100%{transform:scale(.85)}}
              @keyframes ${uid}-drift{0%{transform:translateX(0) translateY(0)}33%{transform:translateX(6px) translateY(-4px)}66%{transform:translateX(-4px) translateY(3px)}100%{transform:translateX(0) translateY(0)}}
            </style>
            <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
              <defs>
                <radialGradient id="${uid}-meadow" cx="50%" cy="80%" r="80%">
                  <stop offset="0%" stop-color="#2D8A4E" stop-opacity=".12"/>
                  <stop offset="100%" stop-color="#7B3FA0" stop-opacity=".05"/>
                </radialGradient>
              </defs>
              <rect width="400" height="200" fill="url(#${uid}-meadow)"/>
              <!-- Lotus flowers (India's national flower) -->
              ${[[80,120],[160,100],[200,130],[280,95],[340,115]].map(([x,y],i)=>`
                <g transform="translate(${x},${y})" style="animation:${uid}-bloom ${2+i*0.3}s ease-in-out infinite;animation-delay:${i*0.4}s">
                  ${[-30,-15,0,15,30].map(angle=>`<ellipse cx="0" cy="-14" rx="9" ry="16" fill="${['#FF69B4','#FF8C9E','#FFB6C1'][Math.abs(angle/15)%3]}" opacity=".75" transform="rotate(${angle})"/>`).join('')}
                  <circle cx="0" cy="0" r="8" fill="#FFD700" opacity=".85"/>
                </g>
              `).join('')}
              <!-- Peacock feathers -->
              ${[[330,40],[360,70]].map(([x,y],i)=>`
                <g transform="translate(${x},${y})" style="transform-origin:${x}px ${y+30}px;animation:${uid}-sway ${2.5+i*0.5}s ease-in-out infinite;animation-delay:${i*0.5}s">
                  <ellipse cx="0" cy="0" rx="12" ry="35" fill="#1A8A5A" opacity=".6"/>
                  <ellipse cx="0" cy="-18" rx="8" ry="14" fill="#4B9CD3" opacity=".7"/>
                  <circle cx="0" cy="-18" r="5" fill="#2A3FA0" opacity=".8"/>
                  <circle cx="0" cy="-18" r="2.5" fill="#D4AF37" opacity=".9"/>
                </g>
              `).join('')}
              <!-- Butterfly -->
              <g transform="translate(100,55)" style="animation:${uid}-drift 4s ease-in-out infinite">
                <path d="M0,0 Q-20,-20 -35,-5 Q-20,10 0,0Z" fill="#FF6B35" opacity=".7"/>
                <path d="M0,0 Q20,-20 35,-5 Q20,10 0,0Z" fill="#FF6B35" opacity=".7"/>
                <path d="M0,0 Q-12,8 -18,20 Q-8,18 0,0Z" fill="#FF8C42" opacity=".6"/>
                <path d="M0,0 Q12,8 18,20 Q8,18 0,0Z" fill="#FF8C42" opacity=".6"/>
              </g>
              <text x="200" y="188" text-anchor="middle" font-size="13" fill="#2D8A4E" opacity=".45" font-family="Georgia,serif" font-style="italic">${Utils.escapeHtml(name)}</text>
            </svg>
          </div>`,
      };

      return banners[cat] || banners['regional'];
    },

    /** Escape HTML special characters */
    escapeHtml(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    /** Convert plain text with newlines to HTML paragraphs */
    textToHtml(str) {
      if (!str) return '';
      return str
        .split(/\n\s*\n/)
        .map(p => p.trim())
        .filter(Boolean)
        .map(p => `<p>${Utils.escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
        .join('');
    },

    /** Convert "- item" list text to <ul> */
    bulletListToHtml(str) {
      if (!str) return '';
      const lines = str.split('\n').map(l => l.trim()).filter(Boolean);
      const items = lines
        .filter(l => l.startsWith('-') || l.startsWith('\u2022'))
        .map(l => `<li>${Utils.escapeHtml(l.replace(/^[-\u2022]\s*/, ''))}</li>`);
      return items.length ? `<ul>${items.join('')}</ul>` : Utils.textToHtml(str);
    },

    /** Convert numbered "1. Step" text to <ol> */
    numberedListToHtml(str) {
      if (!str) return '';
      const lines = str.split('\n').map(l => l.trim()).filter(Boolean);
      const items = lines
        .filter(l => /^\d+\./.test(l))
        .map(l => `<li>${Utils.escapeHtml(l.replace(/^\d+\.\s*/, ''))}</li>`);
      return items.length ? `<ol>${items.join('')}</ol>` : Utils.textToHtml(str);
    },

    /**
     * Parse Shubh Muhurat text into rows.
     * Expected format per line: "Name | Time | Notes"
     * Falls back to rendering as text if format not matched.
     */
    parseMuhurat(str) {
      if (!str) return null;
      const lines = str.split('\n').map(l => l.trim()).filter(Boolean);
      const rows = lines
        .filter(l => l.includes('|'))
        .map(l => {
          const parts = l.split('|').map(p => p.trim());
          return { name: parts[0] || '', time: parts[1] || '', notes: parts[2] || '' };
        });
      return rows.length ? rows : null;
    },

    /**
     * Parse Do's & Don'ts text.
     * Expected format:
     *   DO:\n- item\n\nDON'T:\n- item
     */
    parseDosAndDonts(str) {
      if (!str) return { dos: [], donts: [] };
      const doMatch    = str.match(/DO[S]?:?\n?([\s\S]*?)(?=DON'?T[S]?:|$)/i);
      const dontMatch  = str.match(/DON'?T[S]?:?\n?([\s\S]*?)$/i);
      const parseList  = (block) => {
        if (!block) return [];
        return block.split('\n')
          .map(l => l.trim().replace(/^[-\u2022]\s*/, ''))
          .filter(Boolean);
      };
      return {
        dos:   parseList(doMatch   ? doMatch[1]   : ''),
        donts: parseList(dontMatch ? dontMatch[1] : ''),
      };
    },

    /**
     * Parse recipe text into ingredients + method.
     * Expected format:
     *   INGREDIENTS:\n- item\n\nMETHOD:\n1. step
     */
    parseRecipe(str) {
      if (!str) return { ingredients: [], method: [] };
      const ingMatch  = str.match(/INGREDIENTS?:?\n?([\s\S]*?)(?=METHOD:|\n\n\d|$)/i);
      const methMatch = str.match(/METHOD:?\n?([\s\S]*?)$/i);
      const parseUl   = (block) => {
        if (!block) return [];
        return block.split('\n')
          .map(l => l.trim().replace(/^[-\u2022]\s*/, ''))
          .filter(Boolean);
      };
      const parseOl   = (block) => {
        if (!block) return [];
        return block.split('\n')
          .map(l => l.trim().replace(/^\d+\.\s*/, ''))
          .filter(Boolean);
      };
      return {
        ingredients: parseUl(ingMatch  ? ingMatch[1]  : ''),
        method:      parseOl(methMatch ? methMatch[1] : ''),
      };
    },

    /**
     * Parse regional notes into {state, text} pairs.
     * Expected format: "State Name: description paragraph"
     */
    parseRegionalNotes(str) {
      if (!str) return [];
      const paragraphs = str.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
      return paragraphs.map(p => {
        const colonIdx = p.indexOf(':');
        if (colonIdx > -1 && colonIdx < 50) {
          return {
            state: p.slice(0, colonIdx).trim(),
            text:  p.slice(colonIdx + 1).trim(),
          };
        }
        return { state: '', text: p };
      });
    },

    /** Format INR price string */
    formatPrice(amount, currencyCode) {
      const num = parseFloat(amount);
      if (isNaN(num)) return amount;
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currencyCode || 'INR',
        maximumFractionDigits: 0,
      }).format(num / 100);
    },

    /** Count how many states the festival is in */
    stateCount(festival) {
      const states = festival.states || [];
      return states.filter(s => s !== 'all-india').length;
    },

    /** Get state display name from slug */
    stateDisplayName(slug) {
      const names = {
        'andhra-pradesh':       'Andhra Pradesh',
        'arunachal-pradesh':    'Arunachal Pradesh',
        'assam':                'Assam',
        'bihar':                'Bihar',
        'chhattisgarh':         'Chhattisgarh',
        'goa':                  'Goa',
        'gujarat':              'Gujarat',
        'haryana':              'Haryana',
        'himachal-pradesh':     'Himachal Pradesh',
        'jharkhand':            'Jharkhand',
        'karnataka':            'Karnataka',
        'kerala':               'Kerala',
        'madhya-pradesh':       'Madhya Pradesh',
        'maharashtra':          'Maharashtra',
        'manipur':              'Manipur',
        'meghalaya':            'Meghalaya',
        'mizoram':              'Mizoram',
        'nagaland':             'Nagaland',
        'odisha':               'Odisha',
        'punjab':               'Punjab',
        'rajasthan':            'Rajasthan',
        'sikkim':               'Sikkim',
        'tamil-nadu':           'Tamil Nadu',
        'telangana':            'Telangana',
        'tripura':              'Tripura',
        'uttar-pradesh':        'Uttar Pradesh',
        'uttarakhand':          'Uttarakhand',
        'west-bengal':          'West Bengal',
        'jammu-kashmir':        'Jammu & Kashmir',
        'ladakh':               'Ladakh',
        'chandigarh':           'Chandigarh',
        'delhi':                'Delhi',
        'puducherry':           'Puducherry',
        'andaman-nicobar':      'Andaman & Nicobar',
        'lakshadweep':          'Lakshadweep',
        'dadra-nagar-haveli':   'Dadra & NH / D&D',
        'all-india':            'All India',
      };
      return names[slug] || slug;
    },

    /** Trapfocus inside an element */
    trapFocus(el) {
      const focusable = el.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      el.addEventListener('keydown', function handler(e) {
        if (e.key !== 'Tab') return;
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
    },
  };

  /* ============================================================
     MAP CONTROLLER
     Handles SVG map hover, tooltip, and state selection.
  ============================================================ */
  const MapController = {

    tooltip:      null,
    mapSvg:       null,
    resetBtn:     null,
    _tooltipTimer: null,

    init() {
      this.mapSvg   = document.getElementById('fi-india-map');
      this.tooltip  = document.getElementById('fi-tooltip');
      this.resetBtn = document.getElementById('fi-reset-map');

      if (!this.mapSvg) return;

      const states = this.mapSvg.querySelectorAll('.fi-state');
      states.forEach(el => this._bindState(el));
      this._decorateMap(states);

      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', () => this.resetSelection());
      }

      // Hide tooltip when mouse leaves map
      this.mapSvg.addEventListener('mouseleave', () => this._hideTooltip());

      // Global mouse move (used by tooltip positioning)
      document.addEventListener('mousemove', (e) => { this._lastMouse = e; });
    },

    _bindState(el) {
      el.addEventListener('mouseenter', (e) => this._onHover(e, el));
      el.addEventListener('mousemove',  (e) => this._positionTooltip(e));
      el.addEventListener('mouseleave', ()  => this._hideTooltip());
      el.addEventListener('click',      ()  => this._onStateClick(el));
      el.addEventListener('keydown',    (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._onStateClick(el);
        }
      });
    },

    _decorateMap(states) {
      this.mapSvg.classList.add('fi-map-svg--designer');
      const container = this.mapSvg.closest('.fi-map-container');
      if (container && !container.querySelector('.fi-map-story-label')) {
        container.insertAdjacentHTML('afterbegin',
          '<div class="fi-map-story-label"><span class="fi-map-story-label__eyebrow">A living cultural map</span><strong>Hover a region to watch its festival story unfold</strong><span>Click any state or Union Territory to explore its calendar</span></div>');
      }

      const palette = ['#F8D9A7','#F2C9B8','#E8D8B5','#CFE2CF','#D9D0E8','#F5D7C6','#CFE0E6'];
      const markerParts = [];
      states.forEach((el, index) => {
        const slug = el.getAttribute('data-state');
        el.style.setProperty('--fi-state-fill', palette[index % palette.length]);
        try {
          const box = el.getBBox();
          if (!box || !box.width || !box.height) return;
          const x = box.x + box.width / 2;
          const y = box.y + box.height / 2;
          const upcoming = this._getUpcomingFestivals(slug, 1);
          const motif = Utils.festivalMotifType(upcoming[0] || { name: '', category: 'Regional' });
          const small = box.width < 26 || box.height < 20;
          markerParts.push(this._markerMarkup(motif, x, y, small, index));
        } catch (err) {
          // Some tiny/island paths may not expose a measurable box.
        }
      });
      if (markerParts.length && !this.mapSvg.querySelector('.fi-map-markers')) {
        this.mapSvg.insertAdjacentHTML('beforeend', '<g class="fi-map-markers" aria-hidden="true">' + markerParts.join('') + '</g>');
      }
    },

    _markerMarkup(type, x, y, small, index) {
      const paths = {
        diya: '<path d="M-5 2Q0 7 5 2Q3 8 0 8Q-3 8-5 2ZM0 1C-3-2 0-5 0-8C4-4 3-1 0 1Z"/>',
        durga: '<path d="M-7 1Q-4-5-1 1Q-4 5-7 1ZM1 1Q4-5 7 1Q4 5 1 1Z"/><circle cx="0" cy="-6" r="1.5"/>',
        holi: '<path d="M-6 6L5-5M3-7l4 4M-8 8l3 3"/><circle cx="-5" cy="-5" r="2"/>',
        flute: '<path d="M-7 6L7-6M-3 3l-2-2M2-1l-2-2M6-5l-2-2"/>',
        modak: '<path d="M0-8Q3-3 6 1Q6 8 0 9Q-6 8-6 1Q-3-3 0-8Z"/><path d="M0-4v10"/>',
        rakhi: '<path d="M-10 0h5M5 0h5"/><circle cx="0" cy="0" r="5"/><circle cx="0" cy="0" r="2"/>',
        harvest: '<path d="M0 9V-8M0-2Q-5-5-7-8M0 3Q5 0 7-4M0 7Q-5 4-7 1"/>',
        damaru: '<path d="M-6-7Q0-2 6-7L5-10H-5ZM-6 7Q0 2 6 7L5 10H-5ZM-3-4v8M3-4v8"/>',
        bow: '<path d="M-5-8Q7 0-5 8M-5-8v16M-9 0H8M5-3l4 3l-4 3"/>',
        lantern: '<path d="M-4-7h8M-5-4l-3 5l3 7h10l3-7l-3-5ZM0 8v3"/>',
        star: '<path d="M0-9l2.5 6h6L4 1l2 7l-6-4l-6 4l2-7l-4.5-4h6Z"/>',
        flag: '<path d="M-5 9V-9M-4-8Q2-11 7-7Q3-2-4-4Z"/>',
        drum: '<path d="M-7-6Q0-9 7-6L5 7Q0 10-5 7Z"/><path d="M-5-4L5 5M5-4L-5 5"/>',
        lotus: '<path d="M0 7Q-7 0 0-8Q7 0 0 7ZM0 7Q-10 6-8-3Q-2-2 0 7ZM0 7Q10 6 8-3Q2-2 0 7Z"/>',
        flower: '<circle cx="0" cy="0" r="2"/><path d="M0-2Q-4-9 0-10Q4-9 0-2ZM2 0Q9-4 10 0Q9 4 2 0ZM0 2Q4 9 0 10Q-4 9 0 2ZM-2 0Q-9 4-10 0Q-9-4-2 0Z"/>'
      };
      if (small) {
        return '<g class="fi-map-marker fi-map-marker--small" transform="translate(' + x.toFixed(1) + ' ' + y.toFixed(1) + ')" style="--fi-marker-delay:' + ((index % 8) * 0.18) + 's"><circle r="3.2"/></g>';
      }
      return '<g class="fi-map-marker fi-map-marker--' + type + '" transform="translate(' + x.toFixed(1) + ' ' + y.toFixed(1) + ')" style="--fi-marker-delay:' + ((index % 8) * 0.18) + 's"><circle class="fi-map-marker__halo" r="13"/><g class="fi-map-marker__icon">' + (paths[type] || paths.flower) + '</g></g>';
    },

    _onHover(e, el) {
      const slug = el.getAttribute('data-state');
      const name = el.getAttribute('data-state-name') || Utils.stateDisplayName(slug);
      const upcoming = this._getUpcomingFestivals(slug, 3);
      const lead = upcoming[0] || { name: name, category: 'Regional' };
      let html = '<div class="fi-tooltip__glow" aria-hidden="true"></div>' +
        '<div class="fi-tooltip__header">' + Utils.festivalSketch(lead, true) +
          '<div><div class="fi-tooltip__state">' + Utils.escapeHtml(name) + '</div>' +
          '<div class="fi-tooltip__kicker">' + (upcoming.length ? 'Festival moments ahead' : 'Cultural calendar') + '</div></div></div>';

      if (upcoming.length) {
        html += '<ul class="fi-tooltip__festivals">';
        upcoming.forEach((festival, index) => {
          const date = festival.dateGregorian
            ? new Date(festival.dateGregorian + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
            : '';
          html += '<li style="--fi-event-delay:' + (index * 0.08) + 's">' +
            Utils.festivalSketch(festival, true) +
            '<span class="fi-tooltip__event-copy"><strong>' + Utils.escapeHtml(festival.name) + '</strong>' +
            (date ? '<small>' + Utils.escapeHtml(date) + '</small>' : '') +
            '</span></li>';
        });
        html += '</ul><div class="fi-tooltip__hint"><span></span> Click to explore this region</div>';
      } else {
        html += '<div class="fi-tooltip__none">Select this region to view its cultural calendar.</div>';
      }

      this.tooltip.innerHTML = html;
      this.tooltip.style.display = 'block';
      this.tooltip.classList.remove('fi-tooltip--visible');
      requestAnimationFrame(() => this.tooltip.classList.add('fi-tooltip--visible'));
      el.classList.add('fi-state--story-active');
      this._positionTooltip(e);
    },

    _positionTooltip(e) {
      if (!this.tooltip || this.tooltip.style.display === 'none') return;

      const container = this.mapSvg.closest('.fi-map-container');
      const rect      = container.getBoundingClientRect();
      const tx        = e.clientX - rect.left + 14;
      const ty        = e.clientY - rect.top  - 10;
      const maxX      = rect.width  - this.tooltip.offsetWidth  - 10;
      const maxY      = rect.height - this.tooltip.offsetHeight - 10;

      this.tooltip.style.left = Math.min(tx, maxX) + 'px';
      this.tooltip.style.top  = Math.max(0, Math.min(ty, maxY)) + 'px';
    },

    _hideTooltip() {
      if (this.mapSvg) {
        this.mapSvg.querySelectorAll('.fi-state--story-active').forEach(state => state.classList.remove('fi-state--story-active'));
      }
      if (this.tooltip) {
        this.tooltip.classList.remove('fi-tooltip--visible');
        this.tooltip.style.display = 'none';
      }
    },

    /** Get top N upcoming festivals for a state */
    _getUpcomingFestivals(stateSlug, limit) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateRank = festival => {
        const date = new Date(festival.dateGregorian + 'T00:00:00');
        return date < today ? date.getTime() + 1e13 : date.getTime();
      };
      const candidates = DATA
        .filter(festival => festival.dateGregorian)
        .filter(festival => {
          const states = festival.states || [];
          return states.includes(stateSlug) || states.includes('all-india');
        });
      const specific = candidates
        .filter(festival => (festival.states || []).includes(stateSlug))
        .sort((a, b) => dateRank(a) - dateRank(b));
      const nationwide = candidates
        .filter(festival => (festival.states || []).includes('all-india'))
        .sort((a, b) => dateRank(a) - dateRank(b));
      const chosen = [];
      const used = new Set();
      const add = festival => {
        if (!festival) return;
        const key = festival.slug || festival.id || festival.name;
        if (used.has(key)) return;
        used.add(key);
        chosen.push(festival);
      };
      if (specific.length) add(specific[0]);
      [...specific.slice(1), ...nationwide]
        .sort((a, b) => dateRank(a) - dateRank(b))
        .forEach(festival => { if (chosen.length < limit) add(festival); });
      return chosen.slice(0, limit);
    },

    _onStateClick(el) {
      const slug = el.getAttribute('data-state');
      const name = el.getAttribute('data-state-name') || Utils.stateDisplayName(slug);

      // Clear previous selection
      this.mapSvg.querySelectorAll('.fi-state--selected').forEach(s => {
        s.classList.remove('fi-state--selected');
      });

      if (AppState.selectedState === slug) {
        // Deselect \u2014 show all
        this.resetSelection();
      } else {
        el.classList.add('fi-state--selected');
        AppState.selectedState = slug;
        this._updateStateName(name);
        FilterController.filterAndRender();
        // Scroll to panel
        const panel = document.getElementById('fi-festival-panel');
        if (panel) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    },

    resetSelection() {
      AppState.selectedState = null;
      if (this.mapSvg) {
        this.mapSvg.querySelectorAll('.fi-state--selected').forEach(s => {
          s.classList.remove('fi-state--selected');
        });
      }
      this._updateStateName('All Festivals of India');
      FilterController.filterAndRender();
    },

    _updateStateName(name) {
      const el = document.getElementById('fi-selected-state-name');
      if (el) el.textContent = name;
    },
  };

  /* ============================================================
     FILTER CONTROLLER
     Handles month + category filter logic and triggers render.
  ============================================================ */
  const FilterController = {

    monthSelect: null,
    catTabs:     null,

    init() {
      this.monthSelect = document.getElementById('fi-month-filter');
      this.catTabs     = document.querySelectorAll('.fi-cat-tab');

      if (this.monthSelect) {
        this.monthSelect.addEventListener('change', () => {
          AppState.selectedMonth = this.monthSelect.value;
          this.filterAndRender();
        });
      }

      this.catTabs.forEach(tab => {
        tab.addEventListener('click', () => {
          this.catTabs.forEach(t => {
            t.classList.remove('fi-cat-tab--active');
            t.setAttribute('aria-selected', 'false');
          });
          tab.classList.add('fi-cat-tab--active');
          tab.setAttribute('aria-selected', 'true');
          AppState.selectedCategory = tab.dataset.cat;
          this.filterAndRender();
        });
      });

      const clearBtn = document.getElementById('fi-clear-filters');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => this.clearAll());
      }
    },

    /** Apply all active filters and re-render */
    filterAndRender() {
      let filtered = [...DATA];

      // Filter by selected state
      if (AppState.selectedState) {
        filtered = filtered.filter(f => {
          const states = f.states || [];
          return states.includes(AppState.selectedState) || states.includes('all-india');
        });
      }

      // Filter by category
      if (AppState.selectedCategory) {
        filtered = filtered.filter(f =>
          (f.category || '').toLowerCase() === AppState.selectedCategory.toLowerCase()
        );
      }

      // Filter by month
      if (AppState.selectedMonth) {
        filtered = filtered.filter(f => {
          const m = Utils.getMonth(f.dateGregorian);
          return m !== null && String(m) === AppState.selectedMonth;
        });
      }

      // Sort chronologically
      filtered.sort((a, b) => {
        if (!a.dateGregorian) return 1;
        if (!b.dateGregorian) return -1;
        return a.dateGregorian.localeCompare(b.dateGregorian);
      });

      AppState.currentFestivals = filtered;
      CardRenderer.render(filtered);
    },

    clearAll() {
      AppState.selectedCategory = '';
      AppState.selectedMonth    = '';
      if (this.monthSelect) this.monthSelect.value = '';
      this.catTabs.forEach(t => {
        t.classList.remove('fi-cat-tab--active');
        t.setAttribute('aria-selected', 'false');
      });
      const allTab = document.querySelector('.fi-cat-tab[data-cat=""]');
      if (allTab) {
        allTab.classList.add('fi-cat-tab--active');
        allTab.setAttribute('aria-selected', 'true');
      }
      this.filterAndRender();
    },
  };

  /* ============================================================
     CARD RENDERER
     Renders festival cards into the .fi-cards-grid container.
  ============================================================ */
  const CardRenderer = {

    grid:      null,
    noResults: null,
    loading:   null,

    init() {
      this.grid      = document.getElementById('fi-cards-grid');
      this.noResults = document.getElementById('fi-no-results');
      this.loading   = document.getElementById('fi-loading');
    },

    render(festivals) {
      if (!this.grid) return;

      // Hide loading
      if (this.loading) this.loading.style.display = 'none';

      if (!festivals.length) {
        this.grid.innerHTML = '';
        if (this.noResults) this.noResults.style.display = 'flex';
        return;
      }

      if (this.noResults) this.noResults.style.display = 'none';

      // Build cards HTML
      const html = festivals.map((f, idx) => this._buildCard(f, idx)).join('');
      this.grid.innerHTML = html;

      // Bind "View Details" buttons
      this.grid.querySelectorAll('.fi-view-details-btn').forEach(btn => {
        const idx = parseInt(btn.dataset.idx, 10);
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          DetailPanel.open(idx);
        });
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            DetailPanel.open(idx);
          }
        });
      });

      // Whole-card click
      this.grid.querySelectorAll('.fi-card').forEach(card => {
        card.addEventListener('click', () => {
          const idx = parseInt(card.dataset.idx, 10);
          DetailPanel.open(idx);
        });
      });
    },

    _buildCard(festival, idx) {
      const catClass  = Utils.categoryClass(festival.category);
      const dateFormatted = Utils.formatDateShort(festival.dateGregorian);
      const brief     = (festival.brief || '').slice(0, 130);
      const stateCount = Utils.stateCount(festival);
      const stateNote = stateCount > 1
        ? `Observed across ${stateCount} regions`
        : '';

      return `
        <article
          id="festival-${Utils.escapeHtml(festival.slug || '')}"
          class="fi-card"
          data-idx="${idx}"
          data-festival-slug="${Utils.escapeHtml(festival.slug || '')}"
          role="article"
          aria-label="Festival: ${Utils.escapeHtml(festival.name)}"
          tabindex="0"
        >
          <div class="fi-card__top">
            <span class="fi-card__category-badge fi-card__category-badge--${catClass}">
              ${Utils.escapeHtml(festival.category || 'Festival')}
            </span>
            <div class="fi-card__title-row">
              <h3 class="fi-card__name">${Utils.escapeHtml(festival.name)}</h3>
              ${Utils.festivalSketch(festival, true)}
            </div>
            ${dateFormatted
              ? `<div class="fi-card__date-greg">${dateFormatted}</div>`
              : ''}
            ${festival.dateLunar
              ? `<div class="fi-card__date-lunar">${Utils.escapeHtml(festival.dateLunar)}</div>`
              : ''}
          </div>

          <div class="fi-card__body">
            ${brief
              ? `<p class="fi-card__brief">${Utils.escapeHtml(brief)}${festival.brief && festival.brief.length > 130 ? '\u2026' : ''}</p>`
              : ''}
            ${stateNote
              ? `<p class="fi-card__states-note">&#x2726; ${stateNote}</p>`
              : ''}
          </div>

          <div class="fi-card__footer">
            <button
              class="fi-btn fi-btn--primary fi-view-details-btn"
              data-idx="${idx}"
              type="button"
              aria-label="View details for ${Utils.escapeHtml(festival.name)}"
            >
              View Details
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </article>
      `;
    },
  };

  /* ============================================================
     DETAIL PANEL
     Slide-in/bottom-sheet panel with tabbed content.
  ============================================================ */
  const DetailPanel = {

    panel:   null,
    overlay: null,
    _prevFocus: null,

    init() {
      this.panel   = document.getElementById('fi-detail-panel');
      this.overlay = document.getElementById('fi-overlay');

      if (!this.panel) return;

      // Close button
      const closeBtn = document.getElementById('fi-detail-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }

      // Overlay click to close
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Keyboard close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.panel.classList.contains('fi-detail-panel--open')) {
          this.close();
        }
      });

      // Nav buttons (prev / next)
      const prevBtn = document.getElementById('fi-detail-prev');
      const nextBtn = document.getElementById('fi-detail-next');
      if (prevBtn) prevBtn.addEventListener('click', () => this.navigate(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.navigate(1));

      // Tab switching
      this.panel.addEventListener('click', (e) => {
        const tab = e.target.closest('.fi-panel-tab');
        if (tab) this._switchTab(tab);
      });
    },

    /** Open the detail panel for festival at index in currentFestivals */
    open(idx) {
      const festival = AppState.currentFestivals[idx];
      if (!festival) return;

      AppState.detailIndex   = idx;
      AppState.detailFestival = festival;

      this._renderContent(festival);
      this._updateNavButtons();

      this.panel.classList.add('fi-detail-panel--open');
      this.panel.setAttribute('aria-hidden', 'false');

      if (this.overlay) {
        this.overlay.classList.add('fi-overlay--visible');
        this.overlay.setAttribute('aria-hidden', 'false');
      }

      document.body.classList.add('fi-panel-open');

      // Focus management
      this._prevFocus = document.activeElement;
      const closeBtn = document.getElementById('fi-detail-close');
      if (closeBtn) closeBtn.focus();

      // Load products async
      if (CONFIG.showProducts) {
        ProductLoader.load(festival.relatedProductTags || [], festival.slug, festival);
      }
    },

    close() {
      this.panel.classList.remove('fi-detail-panel--open');
      this.panel.setAttribute('aria-hidden', 'true');

      if (this.overlay) {
        this.overlay.classList.remove('fi-overlay--visible');
        this.overlay.setAttribute('aria-hidden', 'true');
      }

      document.body.classList.remove('fi-panel-open');

      // Return focus
      if (this._prevFocus) this._prevFocus.focus();
    },

    navigate(direction) {
      const newIdx = AppState.detailIndex + direction;
      if (newIdx < 0 || newIdx >= AppState.currentFestivals.length) return;
      this.open(newIdx);
      // Scroll panel body to top
      const body = this.panel.querySelector('.fi-detail-panel__body');
      if (body) body.scrollTop = 0;
    },

    _updateNavButtons() {
      const prevBtn = document.getElementById('fi-detail-prev');
      const nextBtn = document.getElementById('fi-detail-next');
      const idx     = AppState.detailIndex;
      const total   = AppState.currentFestivals.length;

      if (prevBtn) prevBtn.disabled = idx <= 0;
      if (nextBtn) nextBtn.disabled = idx >= total - 1;
    },

    _switchTab(tab) {
      const tabId = tab.dataset.tab;
      if (!tabId) return;

      // Deactivate all
      this.panel.querySelectorAll('.fi-panel-tab').forEach(t => {
        t.classList.remove('fi-panel-tab--active');
        t.setAttribute('aria-selected', 'false');
      });
      this.panel.querySelectorAll('.fi-tab-pane').forEach(p => {
        p.classList.remove('fi-tab-pane--active');
      });

      // Activate selected
      tab.classList.add('fi-panel-tab--active');
      tab.setAttribute('aria-selected', 'true');
      const pane = document.getElementById(`fi-tab-${tabId}`);
      if (pane) pane.classList.add('fi-tab-pane--active');
    },

    /** Build and inject full panel HTML for a festival */
    _renderContent(f) {
      const catClass      = Utils.categoryClass(f.category);
      const dateFormatted = Utils.formatDate(f.dateGregorian);
      const muhuratRows   = Utils.parseMuhurat(f.shubhMuhurat);
      const recipe        = Utils.parseRecipe(f.prasadRecipe);
      const dosdonts      = Utils.parseDosAndDonts(f.dosAndDonts);
      const regional      = Utils.parseRegionalNotes(f.regionalNotes);
      const poojaItems    = (f.poojaSetup || '').split('\n').filter(l => l.trim());
      const stateCount    = Utils.stateCount(f);

      /* \u2500\u2500 Header \u2500\u2500 */
      const headerHTML = `
        <div class="fi-detail-panel__header" id="fi-panel-header">
          <div class="fi-detail-panel__top-bar">
            <div class="fi-detail-panel__meta">
              <span class="fi-card__category-badge fi-card__category-badge--${catClass}" style="margin-bottom:.5rem;display:inline-flex;">
                ${Utils.escapeHtml(f.category || 'Festival')}
              </span>
              <div class="fi-detail-panel__title-row">
                <h2 class="fi-detail-panel__festival-name" id="fi-panel-title">
                  ${Utils.escapeHtml(f.name)}
                </h2>
                ${Utils.festivalSketch(f, false)}
              </div>
              <div class="fi-detail-panel__dates">
                ${dateFormatted
                  ? `<span class="fi-detail-panel__date-greg"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" style="vertical-align:-1px;margin-right:3px;"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>${dateFormatted}</span>`
                  : ''}
                ${f.dateLunar
                  ? `<span class="fi-detail-panel__date-lunar">${Utils.escapeHtml(f.dateLunar)}</span>`
                  : ''}
                ${stateCount > 1
                  ? `<span class="fi-card__states-note">&#x2726; ${stateCount} states</span>`
                  : ''}
              </div>
            </div>
            <div class="fi-detail-panel__nav" aria-label="Navigate between festivals">
              <button id="fi-detail-prev" class="fi-detail-panel__nav-btn" type="button" aria-label="Previous festival" title="Previous">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <button id="fi-detail-next" class="fi-detail-panel__nav-btn" type="button" aria-label="Next festival" title="Next">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
            <button id="fi-detail-close" class="fi-detail-panel__close" type="button" aria-label="Close festival details">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>

          <!-- Tab bar -->
          <nav class="fi-panel-tabs" role="tablist" aria-label="Festival information tabs">
            <button class="fi-panel-tab fi-panel-tab--active" data-tab="overview"    role="tab" aria-selected="true"  aria-controls="fi-tab-overview">Overview</button>
            <button class="fi-panel-tab" data-tab="muhurat"     role="tab" aria-selected="false" aria-controls="fi-tab-muhurat">Observance Times</button>
            <button class="fi-panel-tab" data-tab="rituals"     role="tab" aria-selected="false" aria-controls="fi-tab-rituals">Rituals</button>
            <button class="fi-panel-tab" data-tab="prasad"      role="tab" aria-selected="false" aria-controls="fi-tab-prasad">Food & Offerings</button>
            <button class="fi-panel-tab" data-tab="pooja"       role="tab" aria-selected="false" aria-controls="fi-tab-pooja">Preparation & Setting</button>
            <button class="fi-panel-tab" data-tab="regional"    role="tab" aria-selected="false" aria-controls="fi-tab-regional">Regional Notes</button>
            ${CONFIG.showProducts
              ? `<button class="fi-panel-tab" data-tab="products" role="tab" aria-selected="false" aria-controls="fi-tab-products">HeritageKosh Picks</button>`
              : ''}
          </nav>
        </div>
      `;

      /* \u2500\u2500 Tab 1: Overview \u2500\u2500 */
      const overviewHTML = `
        <div class="fi-tab-pane fi-tab-pane--active" id="fi-tab-overview" role="tabpanel" aria-labelledby="tab-overview">
          ${f.bannerImage
            ? `<img src="${f.bannerImage}" alt="${Utils.escapeHtml(f.name)} banner" class="fi-detail-banner" loading="lazy"/>`
            : Utils.categoryBanner(f)}

          ${f.brief
            ? `<blockquote class="fi-brief-block">${Utils.escapeHtml(f.brief)}</blockquote>`
            : ''}

          ${f.significance
            ? `<h3 class="fi-section-heading">Significance</h3>
               <div class="fi-prose">${Utils.textToHtml(f.significance)}</div>`
            : ''}

          ${f.otherNotes
            ? `<h3 class="fi-section-heading" style="margin-top:1.25rem;">Additional Notes</h3>
               <div class="fi-prose">${Utils.textToHtml(f.otherNotes)}</div>`
            : ''}
        </div>
      `;

      /* \u2500\u2500 Tab 2: Shubh Muhurat \u2500\u2500 */
      let muhuratContent = '';
      if (muhuratRows && muhuratRows.length) {
        muhuratContent = `
          <table class="fi-muhurat-table" aria-label="Observance times">
            <thead>
              <tr>
                <th scope="col">Occasion</th>
                <th scope="col">Timing</th>
                <th scope="col">Notes</th>
              </tr>
            </thead>
            <tbody>
              ${muhuratRows.map(r => `
                <tr>
                  <td>${Utils.escapeHtml(r.name)}</td>
                  <td>${Utils.escapeHtml(r.time)}</td>
                  <td>${Utils.escapeHtml(r.notes)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
      } else if (f.shubhMuhurat) {
        muhuratContent = `<div class="fi-prose">${Utils.textToHtml(f.shubhMuhurat)}</div>`;
      } else {
        muhuratContent = `<p class="fi-prose" style="color:var(--fi-text-muted);font-style:italic;">No specific observance time is recorded for this entry.</p>`;
      }

      const muhuratHTML = `
        <div class="fi-tab-pane" id="fi-tab-muhurat" role="tabpanel" aria-labelledby="tab-muhurat">
          <h3 class="fi-section-heading">Observance Times</h3>
          ${muhuratContent}
          <div class="fi-muhurat-note" role="note">
            \u2139\ufe0f The description above states whether this is an all-day, daytime, evening, night-time, multi-day or institution-scheduled observance.
          </div>
        </div>
      `;

      /* \u2500\u2500 Tab 3: Rituals \u2500\u2500 */
      const ritualsHTML = `
        <div class="fi-tab-pane" id="fi-tab-rituals" role="tabpanel" aria-labelledby="tab-rituals">
          ${f.rituals
            ? `<h3 class="fi-section-heading">Rituals & Traditions</h3>
               <div class="fi-prose">${Utils.numberedListToHtml(f.rituals)}</div>`
            : '<p style="color:var(--fi-text-muted);font-style:italic;">Ritual details coming soon.</p>'}

          ${(dosdonts.dos.length || dosdonts.donts.length)
            ? `<h3 class="fi-section-heading" style="margin-top:1.5rem;">Respectful Participation</h3>
               <div class="fi-dos-donts">
                 <div class="fi-dos">
                   <div class="fi-dos__title">\u2713 Do's</div>
                   <ul>${dosdonts.dos.map(d => `<li>${Utils.escapeHtml(d)}</li>`).join('')}</ul>
                 </div>
                 <div class="fi-donts">
                   <div class="fi-donts__title">\u2717 Don'ts</div>
                   <ul>${dosdonts.donts.map(d => `<li>${Utils.escapeHtml(d)}</li>`).join('')}</ul>
                 </div>
               </div>`
            : ''}
        </div>
      `;

      /* \u2500\u2500 Tab 4: Prasad & Recipe \u2500\u2500 */
      let recipeContent = '';
      if (recipe.ingredients.length || recipe.method.length) {
        recipeContent = `
          ${recipe.ingredients.length
            ? `<div class="fi-recipe-ingredients">
                 <div class="fi-recipe-title">Ingredients</div>
                 <ul>${recipe.ingredients.map(i => `<li>${Utils.escapeHtml(i)}</li>`).join('')}</ul>
               </div>`
            : ''}
          ${recipe.method.length
            ? `<div class="fi-recipe-method">
                 <div class="fi-recipe-title">Method</div>
                 <ol>${recipe.method.map(s => `<li>${Utils.escapeHtml(s)}</li>`).join('')}</ol>
               </div>`
            : ''}
        `;
      } else if (f.prasadRecipe) {
        recipeContent = `<div class="fi-prose">${Utils.textToHtml(f.prasadRecipe)}</div>`;
      }

      const prasadHTML = `
        <div class="fi-tab-pane" id="fi-tab-prasad" role="tabpanel" aria-labelledby="tab-prasad">
          ${f.prasadName
            ? `<div class="fi-prasad-badge" aria-label="Food or offering: ${Utils.escapeHtml(f.prasadName)}">
                 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" style="vertical-align:-2px;margin-right:4px;"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z"/><path d="M12 6v6l4 2"/></svg>${Utils.escapeHtml(f.prasadName)}
               </div>`
            : ''}
          ${recipeContent || '<p style="color:var(--fi-text-muted);font-style:italic;">Food or offering details are not recorded for this entry.</p>'}
        </div>
      `;

      /* \u2500\u2500 Tab 5: Pooja Setup \u2500\u2500 */
      let poojaContent = '';
      if (poojaItems.length) {
        poojaContent = `
          <ul class="fi-checklist" aria-label="Preparation and setting checklist">
            ${poojaItems.map(item => {
              const clean = item.trim().replace(/^[-\u2022\u2726]\s*/, '');
              return clean ? `<li>${Utils.escapeHtml(clean)}</li>` : '';
            }).filter(Boolean).join('')}
          </ul>
        `;
      } else if (f.poojaSetup) {
        poojaContent = `<div class="fi-prose">${Utils.textToHtml(f.poojaSetup)}</div>`;
      } else {
        poojaContent = '<p style="color:var(--fi-text-muted);font-style:italic;">Preparation and setting details are not recorded for this entry.</p>';
      }

      const poojaHTML = `
        <div class="fi-tab-pane" id="fi-tab-pooja" role="tabpanel" aria-labelledby="tab-pooja">
          <h3 class="fi-section-heading">Altar & Pooja Setup</h3>
          ${poojaContent}
        </div>
      `;

      /* \u2500\u2500 Tab 6: Regional Notes \u2500\u2500 */
      let regionalContent = '';
      if (regional.length) {
        regionalContent = regional.map(r => `
          <div class="fi-regional-item">
            ${r.state ? `<div class="fi-regional-item__state"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" style="vertical-align:-1px;margin-right:4px;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>${Utils.escapeHtml(r.state)}</div>` : ''}
            <div class="fi-regional-item__text">${Utils.escapeHtml(r.text)}</div>
          </div>
        `).join('');
      } else {
        regionalContent = '<p style="color:var(--fi-text-muted);font-style:italic;">Regional notes coming soon.</p>';
      }

      const regionalHTML = `
        <div class="fi-tab-pane" id="fi-tab-regional" role="tabpanel" aria-labelledby="tab-regional">
          <h3 class="fi-section-heading">How It's Celebrated Across India</h3>
          ${regionalContent}
        </div>
      `;

      /* \u2500\u2500 Tab 7: HeritageKosh Picks \u2500\u2500 */
      const productsHTML = CONFIG.showProducts ? `
        <div class="fi-tab-pane" id="fi-tab-products" role="tabpanel" aria-labelledby="tab-products">
          <div class="fi-products-section">
            <h3 class="fi-products-section__title">&#x2726; Fresh Picks for ${Utils.escapeHtml(f.name)}</h3>
            <p class="fi-products-section__note">${(f.category || '').toLowerCase() === 'hindu'
              ? 'A rotating mix of occasion-specific pieces and lighter festive accents. Refreshed weekly.'
              : 'Carefully matched to this occasion and its setting.'}</p>
            <div id="fi-products-container-${Utils.escapeHtml(f.slug || f.id)}" class="fi-products-grid">
              <div class="fi-products-loading" style="grid-column:1/-1;">
                <span class="fi-loading-spinner"></span>
                Curating this week’s selection…
              </div>
            </div>
          </div>
        </div>
      ` : '';

      /* \u2500\u2500 Assemble panel body \u2500\u2500 */
      const bodyHTML = `
        <div class="fi-detail-panel__body" id="fi-panel-body">
          ${overviewHTML}
          ${muhuratHTML}
          ${ritualsHTML}
          ${prasadHTML}
          ${poojaHTML}
          ${regionalHTML}
          ${productsHTML}
        </div>
      `;

      this.panel.innerHTML = headerHTML + bodyHTML;

      // Re-bind close + nav + tabs (since innerHTML was replaced)
      const closeBtn = document.getElementById('fi-detail-close');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());

      const prevBtn = document.getElementById('fi-detail-prev');
      const nextBtn = document.getElementById('fi-detail-next');
      if (prevBtn) prevBtn.addEventListener('click', () => this.navigate(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.navigate(1));
      this._updateNavButtons();

      this.panel.querySelectorAll('.fi-panel-tab').forEach(tab => {
        tab.addEventListener('click', () => this._switchTab(tab));
      });

      // Trap focus
      Utils.trapFocus(this.panel);
    },
  };

  /* ============================================================
     PRODUCT LOADER
     Fetches products using Shopify's Search & Discovery API
     by tag. Falls back gracefully if API is unavailable.
  ============================================================ */
  const ProductLoader = {
    _catalogPromise: null,

    /** Load rotating Hindu picks or tradition-neutral tag matches for other festivals. */
    async load(tags, festivalSlug, festival) {
      const container = document.getElementById('fi-products-container-' + festivalSlug);
      if (!container) return;
      const isHindu = ((festival && festival.category) || '').toLowerCase() === 'hindu';

      if (isHindu) {
        try {
          const catalog = await this._getCatalog();
          const picks = this._selectHinduPicks(catalog, tags, festival);
          if (picks.length) {
            this._renderFromProductsAPI(container, picks);
            return;
          }
        } catch (err) {
          // Continue to tagged search when the full catalog endpoint is unavailable.
        }
      }

      await this._loadTagged(container, tags, festivalSlug);
    },

    async _getCatalog() {
      if (!this._catalogPromise) {
        this._catalogPromise = fetch('/collections/all/products.json?limit=250')
          .then(res => {
            if (!res.ok) throw new Error('Product catalog unavailable');
            return res.json();
          })
          .then(data => (data.products || []).filter(p => p && p.available !== false));
      }
      return this._catalogPromise;
    },

    _hash(value) {
      let h = 2166136261;
      const str = String(value || '');
      for (let i = 0; i < str.length; i += 1) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return h >>> 0;
    },

    _weekKey() {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1);
      return now.getFullYear() + '-w' + Math.ceil((((now - start) / 86400000) + start.getDay() + 1) / 7);
    },

    _occasionKeywords(festival, tags) {
      const name = ((festival && festival.name) || '').toLowerCase();
      const groups = [
        [/diwali|deepavali|deepam|deepotsav|kali puja/, ['diya','light','lamp','rangoli','toran','shubh labh','aroma','camphor']],
        [/navratri|dussehra|dasara|durga|bonalu/, ['garland','marigold','lotus','kalash','bell','toran','dhoop','diya']],
        [/janmashtami|krishna|govardhan/, ['peacock','lotus','garland','bell','incense','mogra','sandalwood','diya']],
        [/ganesh|vinayaka/, ['marigold','garland','kalash','bell','lotus','toran','incense','diya']],
        [/shivaratri|shiva/, ['camphor','sandalwood','dhoop','incense','diya','lamp','kalash','mala']],
        [/holi|shigmo/, ['flower','garland','rose','jasmine','festive','aroma','light','toran']],
        [/raksha|rakhi|bhai dooj/, ['gift','diya','aroma','flower','toran','rangoli','light']],
        [/pongal|sankranti|bihu|onam|baisakhi|nuakhai/, ['banana leaf','kalash','toran','garland','flower','rangoli','lamp']],
        [/ram navami|hanuman|rath yatra/, ['bell','mala','incense','camphor','sandalwood','kalash','diya']],
        [/akshaya|dhanteras|lakshmi/, ['kuber','kalash','diya','lamp','shubh labh','brass','rangoli']]
      ];
      const found = [];
      groups.forEach(group => { if (group[0].test(name)) found.push(...group[1]); });
      (tags || []).forEach(tag => found.push(String(tag).toLowerCase()));
      if (!found.length) found.push('festive','pooja','puja','diya','garland','aroma','toran');
      return [...new Set(found)];
    },

    _productText(product) {
      const tags = Array.isArray(product.tags) ? product.tags.join(' ') : (product.tags || '');
      return ((product.title || '') + ' ' + (product.product_type || product.productType || '') + ' ' + tags).toLowerCase();
    },

    _productPrice(product) {
      const variant = product.variants && product.variants[0];
      return variant ? Number(variant.price || 0) : Number(product.price || 0);
    },

    _selectHinduPicks(products, tags, festival) {
      const keywords = this._occasionKeywords(festival, tags);
      const lightTerms = ['diya','light','lamp','oil','aroma','incense','bell','toran','garland','rangoli','camphor','kapoor','mala','hanging','kalash'];
      const slug = (festival && (festival.slug || festival.name)) || 'festival';
      const seed = this._hash(slug + '-' + this._weekKey());
      const count = 5 + (this._hash(slug) % 2);

      const ranked = products.map(product => {
        const hay = this._productText(product);
        const price = this._productPrice(product);
        let score = 0;
        keywords.forEach(keyword => {
          const clean = String(keyword).replace(/^#/, '').trim();
          if (clean && hay.includes(clean)) score += clean.length > 8 ? 8 : 5;
        });
        const light = price > 0 && price <= 800 || lightTerms.some(term => hay.includes(term));
        if (light) score += 3;
        if (price > 0 && price <= 500) score += 2;
        if (/pooja|puja|festive|traditional|handcrafted|indian/.test(hay)) score += 1;
        const jitter = (this._hash(seed + '-' + (product.handle || product.id || product.title)) % 1000) / 1000;
        return { product, score, jitter, light };
      }).filter(item => item.score > 0);

      ranked.sort((a, b) => (b.score + b.jitter * 2) - (a.score + a.jitter * 2));
      const selected = [];
      const used = new Set();
      const add = (item, type) => {
        if (!item || !item.product) return;
        const key = item.product.handle || item.product.id || item.product.title;
        if (used.has(key)) return;
        item.product._pickType = type;
        used.add(key);
        selected.push(item.product);
      };

      ranked.filter(item => item.score >= 6).slice(0, 3).forEach(item => add(item, 'Festival match'));
      ranked.filter(item => item.light).sort((a, b) => b.jitter - a.jitter).slice(0, 4).forEach(item => {
        if (selected.length < count) add(item, 'Lighter pick');
      });
      ranked.forEach(item => {
        if (selected.length < count) add(item, item.light ? 'Lighter pick' : 'Curated pick');
      });
      return selected.slice(0, count);
    },

    async _loadTagged(container, tags, festivalSlug) {
      if (!tags || !tags.length) {
        this._renderEmpty(container);
        return;
      }
      const tagQuery = tags.map(t => 'tag:' + encodeURIComponent(t)).join(' OR ');
      try {
        const limit = Math.max(3, Math.min(Number(CONFIG.productsPerFestival) || 4, 4));
        const url = '/search/suggest.json?q=' + encodeURIComponent(tagQuery) + '&resources[type]=product&resources[options][limit]=' + limit + '&resources[options][unavailable_products]=hide';
        const res = await fetch(url);
        if (!res.ok) throw new Error('Search API error');
        const data = await res.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (!products.length) throw new Error('No tagged products');
        this._render(container, products);
      } catch (err) {
        this._renderEmpty(container);
      }
    },

    _pickBadge(product) {
      if (!product || !product._pickType) return '';
      const tone = product._pickType === 'Lighter pick' ? ' fi-product-card__pick-badge--light' : '';
      return '<span class="fi-product-card__pick-badge' + tone + '">' + Utils.escapeHtml(product._pickType) + '</span>';
    },

    _render(container, products) {
      container.innerHTML = products.map(p => {
        const imgUrl = p.image && p.image.url ? p.image.url : null;
        const price  = p.price ? Utils.formatPrice(p.price, 'INR') : '';
        return '<a href="' + p.url + '" class="fi-product-card" target="_blank" rel="noopener" aria-label="' + Utils.escapeHtml(p.title) + '">' +
          this._pickBadge(p) +
          (imgUrl
            ? '<img src="' + imgUrl + '" alt="' + Utils.escapeHtml(p.title) + '" class="fi-product-card__img" loading="lazy"/>'
            : '<div class="fi-product-card__img-placeholder" aria-hidden="true">' + Utils.festivalSketch({name: p.title, category: 'Hindu'}, true) + '</div>') +
          '<div class="fi-product-card__body"><div class="fi-product-card__name">' + Utils.escapeHtml(p.title) + '</div>' +
          (price ? '<div class="fi-product-card__price">' + price + '</div>' : '') +
          '</div><div class="fi-product-card__cta">' + Utils.escapeHtml(CONFIG.ctaLabel) + '</div></a>';
      }).join('');
    },

    _renderFromProductsAPI(container, products) {
      container.innerHTML = products.map(p => {
        const imgUrl = p.images && p.images[0] ? p.images[0].src : null;
        const variant = p.variants && p.variants[0];
        const price = variant ? Utils.formatPrice(Number(variant.price) * 100, 'INR') : '';
        return '<a href="/products/' + encodeURIComponent(p.handle) + '" class="fi-product-card" target="_blank" rel="noopener" aria-label="' + Utils.escapeHtml(p.title) + '">' +
          this._pickBadge(p) +
          (imgUrl
            ? '<img src="' + imgUrl + '" alt="' + Utils.escapeHtml(p.title) + '" class="fi-product-card__img" loading="lazy"/>'
            : '<div class="fi-product-card__img-placeholder" aria-hidden="true">' + Utils.festivalSketch({name: p.title, category: 'Hindu'}, true) + '</div>') +
          '<div class="fi-product-card__body"><div class="fi-product-card__name">' + Utils.escapeHtml(p.title) + '</div>' +
          (price ? '<div class="fi-product-card__price">' + price + '</div>' : '') +
          '</div><div class="fi-product-card__cta">' + Utils.escapeHtml(CONFIG.ctaLabel) + '</div></a>';
      }).join('');
    },

    _renderEmpty(container) {
      container.innerHTML = '<div class="fi-products-none" style="grid-column:1/-1;">No products are currently matched to this festival.</div>';
    },
  };


  /* ============================================================
     DEEP LINK CONTROLLER
     Opens and positions the exact festival requested by the
     homepage preview: ?festival=slug#festival-slug
  ============================================================ */
  const DeepLinkController = {

    _requestedSlug() {
      const params = new URLSearchParams(window.location.search);
      const querySlug = params.get('festival');
      if (querySlug) return String(querySlug).trim().toLowerCase();

      const hash = decodeURIComponent(window.location.hash || '');
      if (hash.indexOf('#festival-') === 0) {
        return hash.slice('#festival-'.length).trim().toLowerCase();
      }
      return '';
    },

    _resetFilters() {
      AppState.selectedState = null;
      AppState.selectedCategory = '';
      AppState.selectedMonth = '';

      document.querySelectorAll('.fi-state--selected').forEach(state => {
        state.classList.remove('fi-state--selected');
      });

      const selectedStateName = document.getElementById('fi-selected-state-name');
      if (selectedStateName) selectedStateName.textContent = 'All Festivals of India';

      if (FilterController.monthSelect) FilterController.monthSelect.value = '';
      if (FilterController.catTabs) {
        FilterController.catTabs.forEach(tab => {
          const isAll = !tab.dataset.cat;
          tab.classList.toggle('fi-cat-tab--active', isAll);
          tab.setAttribute('aria-selected', isAll ? 'true' : 'false');
        });
      }

      FilterController.filterAndRender();
    },

    openRequested() {
      const slug = this._requestedSlug();
      if (!slug) return false;

      let idx = AppState.currentFestivals.findIndex(festival =>
        String(festival.slug || '').trim().toLowerCase() === slug
      );

      if (idx < 0 && DATA.some(festival =>
        String(festival.slug || '').trim().toLowerCase() === slug
      )) {
        this._resetFilters();
        idx = AppState.currentFestivals.findIndex(festival =>
          String(festival.slug || '').trim().toLowerCase() === slug
        );
      }

      if (idx < 0) return false;

      const card = Array.from(document.querySelectorAll('.fi-card')).find(node =>
        String(node.dataset.festivalSlug || '').trim().toLowerCase() === slug
      );

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (card) {
        card.scrollIntoView({
          behavior: reducedMotion ? 'auto' : 'smooth',
          block: 'center'
        });
        card.classList.add('fi-card--deep-linked');
      }

      window.setTimeout(() => {
        DetailPanel.open(idx);
      }, reducedMotion ? 0 : 220);

      return true;
    },

    init() {
      this.openRequested();
      window.addEventListener('hashchange', () => this.openRequested());
    },
  };

  /* ============================================================
     INIT
     Bootstraps all modules after DOM is ready.
     If window.festivalsAssetUrl is set, fetches the full 120-festival
     JSON asset (bypasses Shopify Liquid's 50-metaobject limit).
  ============================================================ */
  function ensureCompleteCategoryNavigation() {
    document.querySelectorAll('.fi-filter-category').forEach(group => {
      if (group.querySelector('[data-cat="Jain"],[data-cat="jain"]')) return;
      const regional = group.querySelector('[data-cat="Regional"],[data-cat="regional"]');
      const useLower = regional && regional.getAttribute('data-cat') === 'regional';
      const roleBits = group.getAttribute('role') === 'tablist' ? ' role="tab" aria-selected="false"' : '';
      const html = '<button class="fi-cat-tab" data-cat="' + (useLower ? 'jain' : 'Jain') + '" type="button"' + roleBits + '>' + (useLower ? '◈ Jain' : 'Jain') + '</button>';
      if (regional) regional.insertAdjacentHTML('beforebegin', html);
      else group.insertAdjacentHTML('beforeend', html);
    });

    const legend = document.querySelector('.fi-legend');
    if (legend && !legend.querySelector('.fi-cat--jain')) {
      const regionalItem = legend.querySelector('.fi-cat--regional');
      const html = '<span class="fi-legend__item" role="listitem"><span class="fi-legend__dot fi-cat--jain"></span>Jain</span>';
      if (regionalItem && regionalItem.closest('.fi-legend__item')) {
        regionalItem.closest('.fi-legend__item').insertAdjacentHTML('beforebegin', html);
      } else {
        legend.insertAdjacentHTML('beforeend', html);
      }
    }
  }

  function init() {
    const safeInit = (label, fn) => {
      try { fn(); return true; }
      catch (err) {
        console.error('[HeritageKosh Festivals] ' + label + ' failed:', err);
        const loading = document.getElementById('fi-loading');
        if (loading) loading.textContent = 'Festival display error (' + label + '): ' + (err && err.message ? err.message : err);
        return false;
      }
    };
    safeInit('category navigation', () => ensureCompleteCategoryNavigation());
    safeInit('cultural map', () => MapController.init());
    safeInit('filters', () => FilterController.init());
    safeInit('cards', () => CardRenderer.init());
    safeInit('details', () => DetailPanel.init());

    // If a CDN asset URL is provided, fetch full dataset, then re-render
    if (window.festivalsAssetUrl) {
      fetch(window.festivalsAssetUrl)
        .then(function(r) { return r.json(); })
        .then(function(fullData) {
          if (Array.isArray(fullData) && fullData.length > 0) {
            DATA = fullData;
            FilterController.filterAndRender();
            DeepLinkController.openRequested();
          }
        })
        .catch(function(err) {
          console.warn('[HeritagKosh] Could not load full festival data, using partial set.', err);
        });
    }

    // Initial render with whatever data is available (partial or full)
    safeInit('festival rendering', () => FilterController.filterAndRender());
    safeInit('festival deep link', () => DeepLinkController.init());
  }

  /* \u2500\u2500 Wait for DOM \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
